<script setup lang="ts">
import { useCommentsStore } from "@/store/comment";
import { getFilterQueryParams } from "@/utils/params";
import { useI18n } from "vue-i18n";
import { formatNumberThousandSeparated } from "@/utils/formatter";
import StatisticsTooltip from "./StatisticsTooltip.vue";
import { ref } from "vue";

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    folder: any;
    active: boolean;
    platformKey: string;
    numberOfItems?: number;
    isNavbarExpanded?: boolean;
  }>(),
  {
    numberOfItems: 0,
  },
);

const formattedNumberOfItems = computed(() =>
  props.numberOfItems ? formatNumberThousandSeparated(props.numberOfItems) : "",
);

const statisticsTooltip = "Diese Zahlen sind Statistiken und werden ungefähr alle 10 Minuten aktualisiert.";
const numberRef = ref<HTMLElement>();
const tooltipRef = ref<InstanceType<typeof StatisticsTooltip>>();

const showTooltip = () => {
  tooltipRef.value?.show();
};

const hideTooltip = () => {
  tooltipRef.value?.hide();
};

const commentStore = useCommentsStore();
const route = useRoute();

const isCurrentFolder = computed(() => route.path.startsWith(props.folder.url));

const folderUrl = computed(() => {
  if (isCurrentFolder.value)
    return route.fullPath;
  const queryParams = getFilterQueryParams(props.folder.filters);
  return `${props.folder.url}${queryParams}`;
});

const watchlistPrivacyStateAriaLabel = computed(() => {
  if (!props.folder.hasOwnProperty("isPublic")) return "";
  else if (props.folder.isPublic) return t("watchlists.public_watchlist");
  return t("watchlists.private_watchlist");
});

onMounted(async () => {
  commentStore.folderFilters.push({
    name: props.folder.key,
    filters: {
      statuses: props.folder.filters.statuses || [],
      status: props.folder.filters.status,
      tags: props.folder.filters.tags || [],
      visibilities: props.folder.filters.visibilities || [],
      attributes: props.folder.filters.attributes || [],
      text: props.folder.filters.text || [],
      unsafe: props.folder.filters.unsafe === "true",
      watchlist: props.folder.filters.watchlist,
      watchlistType: props.folder.filters.watchlistType,
      from: props.folder.filters.from || "",
      to: props.folder.filters.to || "",
    },
  });
});
</script>

<template>
  <div
    class="navbar-platform-folder relative w-full rounded-md pl-3 text-[15px] before:absolute before:-left-1.5 before:bottom-0 before:top-0 before:my-auto before:block before:h-1.5 before:w-1.5 before:rounded-full before:bg-ppa-light-grey-2 after:absolute after:-left-1 after:-top-[52%] after:block after:h-[calc(100%+3px)] after:w-0.5 after:bg-ppa-light-grey-2 first:after:top-0 first:after:h-1/2"
    :class="{ 'font-semibold text-ppa-dark-blue': active, 'w-[90%]': !isNavbarExpanded }"
  >
    <RouterLink role="treeitem" tabindex="0" :to="folderUrl"
      class="router-link flex w-[95%] justify-between rounded-md p-2 text-left border-2 border-solid"
      :aria-label="`${watchlistPrivacyStateAriaLabel} ${folder.label} ${numberOfItems} ${$t(
        'navbar.aria_number_of_items',
      )}`"
    >
      <div class="flex max-w-fit truncate" :title="folder.label">
        <slot></slot>
        <div v-if="isNavbarExpanded" class="max-w-fit truncate pl-2">{{ folder.label }}</div>
      </div>
      <span 
        v-if="isNavbarExpanded" 
        ref="numberRef"
        class="right-1 ml-2"
        @mouseenter="showTooltip"
        @mouseleave="hideTooltip"
        aria-describedby="statistics-tooltip"
      >
        {{ formattedNumberOfItems }}
      </span>
    </RouterLink>
    <StatisticsTooltip 
      ref="tooltipRef"
      :text="statisticsTooltip"
      :targetRef="numberRef ?? null"
    />
  </div>
</template>
